import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  LoadingOutlined,
  ScanOutlined,
  StopOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Layout from '@/common/Layout';
import CustomTable from '@/common/Content/CustomTable';
import CustomModal from '@/common/CustomModal';
import useHvacFilterAccess from '@/features/hvacFilter/hooks/useHvacFilterAccess';
import {
  bulkInactivateHvac,
  bulkInstallFromSpare,
  bulkMarkCleanedAndAssign,
  bulkSendForCleaning,
  generateHvacUsageLogReport,
  getHvacAreas,
  getHvacCounts,
  getHvacLabels,
  getHvacLogs,
  reactivateHvac,
  regenerateMissingHvacPdfs
} from '@/features/hvacFilter/redux/slice';
import {
  ACTION_LABELS,
  buildBlobPdfUrl,
  formatDetails,
  getStatusFromRecord,
  STATUS_CONFIG
} from './HvacFilterAllocation.helper';
import HvacScanModal from './HvacScanModal';
import BulkHvacScanFilterModal from './BulkHvacScanFilterModal';
import styles from './index.module.less';

const { TabPane } = Tabs;

const HvacLabelList = ({ labels }) => {
  const visible = labels.slice(0, 10);
  const overflow = labels.length - visible.length;
  return (
    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {visible.map((l) => (
        <Tag key={l} style={{ fontFamily: 'monospace' }}>
          {l}
        </Tag>
      ))}
      {overflow > 0 && <Tag color="default">+{overflow} more</Tag>}
    </div>
  );
};

const HvacFilterAllocation = () => {
  const dispatch = useDispatch();
  const {
    list,
    listPagination,
    loading,
    counts,
    hvacLogs,
    hvacLogsLoading,
    areas,
    pendingCount,
    stalePendingCount,
    reportGenerating
  } = useSelector((state) => state.hvacFilter);

  // Pagination & filter state
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(
    () => Number(localStorage.getItem('hvac_perPage')) || 10
  );
  const [activeTab, setActiveTab] = useState(
    () => localStorage.getItem('hvac_activeTab') || 'all'
  );
  const [labelSearch, setLabelSearch] = useState('');
  const [areaSearchInput, setAreaSearchInput] = useState('');
  const [scanFilter, setScanFilter] = useState(null);
  const [bulkScanModalOpen, setBulkScanModalOpen] = useState(false);

  // Cross-page selection
  const [selectedItems, setSelectedItems] = useState([]);
  const selectedIds = selectedItems.map((i) => i.id);
  const selectedLabels = selectedItems.map((i) => i.hvac_label_number);

  // Modal state
  const [modalType, setModalType] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [logHvacLabel, setLogHvacLabel] = useState(null);
  const [reactivateHvacLabel, setReactivateHvacLabel] = useState(null);

  // Two-step modal state
  const [markCleanedStep, setMarkCleanedStep] = useState('area'); // 'area' | 'scan'
  const [markCleanedArea, setMarkCleanedArea] = useState(null);
  const [installStep, setInstallStep] = useState('area');
  const [installArea, setInstallArea] = useState(null);

  // Forms
  const [reportForm] = Form.useForm();
  const [inactivateForm] = Form.useForm();
  const [reactivateForm] = Form.useForm();

  const { canAct, canReactivate } = useHvacFilterAccess();

  // Row highlight after actions
  const [highlightedLabels, setHighlightedLabels] = useState(new Set());
  const flashTimerRef = useRef(null);
  const flashRows = useCallback((labelNumbers) => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setHighlightedLabels(new Set(labelNumbers));
    flashTimerRef.current = setTimeout(
      () => setHighlightedLabels(new Set()),
      2000
    );
  }, []);

  // Polling for pending PDFs
  const pollRef = useRef(null);

  // Initial load
  useEffect(() => {
    dispatch(getHvacCounts());
    dispatch(getHvacAreas());
  }, [dispatch]);

  // Refetch list whenever filters change
  useEffect(() => {
    fetchList();
  }, [page, perPage, activeTab, labelSearch, areaSearchInput, scanFilter]);

  // Persist perPage
  useEffect(() => {
    localStorage.setItem('hvac_perPage', perPage);
  }, [perPage]);

  // Polling
  useEffect(() => {
    if (pendingCount > 0) {
      if (pollRef.current) return;
      pollRef.current = setInterval(() => {
        fetchList();
        dispatch(getHvacCounts());
      }, 5000);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current && pendingCount === 0) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [pendingCount]);

  useEffect(
    () => () => {
      if (pollRef.current) clearInterval(pollRef.current);
    },
    []
  );

  const fetchList = () => {
    const payload = { page, per_page: perPage };
    if (activeTab !== 'all') payload.tab = activeTab;
    if (labelSearch) payload.search = labelSearch;
    if (areaSearchInput) payload.area_search = areaSearchInput;
    if (scanFilter && scanFilter.length > 0)
      payload.hvac_labels = scanFilter.join(',');
    dispatch(getHvacLabels(payload));
  };

  const refreshAll = () => {
    fetchList();
    dispatch(getHvacCounts());
    dispatch(getHvacAreas());
  };

  // Selection status (similar to drums)
  const selectionStatus = useMemo(() => {
    if (!selectedItems.length) return null;
    const statuses = [...new Set(selectedItems.map((i) => i.status))];
    if (statuses.length > 1) return 'mixed';
    return statuses[0];
  }, [selectedItems]);

  const canMarkCleanedAssign = selectionStatus === 'to_be_cleaned';
  const canInstallFromSpare = selectionStatus === 'in_spare_stock';
  const canSendForCleaning = selectionStatus === 'in_use';
  const isMixed = selectionStatus === 'mixed';

  const canInactivate = useMemo(() => {
    if (!selectedItems.length) return false;
    return selectedItems.every(
      (i) =>
        i.status === 'in_use' ||
        i.status === 'in_spare_stock' ||
        i.status === 'to_be_cleaned'
    );
  }, [selectedItems]);

  // Row selection (cross-page aware)
  const rowSelection = {
    selectedRowKeys: selectedIds,
    onChange: (keys, rows) => {
      const currentPageIds = list.map((r) => r.id);
      const otherPageItems = selectedItems.filter(
        (item) => !currentPageIds.includes(item.id)
      );
      const currentPageSelected = rows.map((r) => ({
        id: r.id,
        status: getStatusFromRecord(r),
        hvac_label_number: r.hvac_label_number
      }));
      setSelectedItems([...otherPageItems, ...currentPageSelected]);
    },
    getCheckboxProps: (record) => ({
      disabled: !record.is_active || record.is_retired
    })
  };

  const clearSelection = () => setSelectedItems([]);

  const handleTabChange = (key) => {
    localStorage.setItem('hvac_activeTab', key);
    setActiveTab(key);
    setPage(1);
    clearSelection();
    setScanFilter(null);
  };

  const handleCardClick = (tab) => {
    handleTabChange(tab);
  };

  const handlePageChange = (p, ps) => {
    setPage(p);
    setPerPage(ps);
  };

  const closeModal = () => {
    if (submitting) return;
    setModalType(null);
    setMarkCleanedStep('area');
    setMarkCleanedArea(null);
    setInstallStep('area');
    setInstallArea(null);
  };

  const hasActiveFilters =
    !!labelSearch ||
    !!areaSearchInput ||
    !!scanFilter ||
    selectedItems.length > 0;

  const handleClearFilters = () => {
    setLabelSearch('');
    setAreaSearchInput('');
    setScanFilter(null);
    clearSelection();
    setPage(1);
  };

  // ---- Bulk: Send for Cleaning ----
  const handleBulkSendForCleaning = async () => {
    setSubmitting(true);
    const res = await dispatch(
      bulkSendForCleaning({ hvac_label_numbers: selectedLabels })
    );
    setSubmitting(false);
    if (res?.payload?.success) {
      flashRows(selectedLabels);
      clearSelection();
      refreshAll();
      closeModal();
    }
  };

  // ---- Bulk: Mark Cleaned & Assign (2 steps) ----
  const handleMarkCleanedStep1 = () => {
    if (!markCleanedArea) return;
    setMarkCleanedStep('scan');
  };
  const handleMarkCleanedWithScan = async (confirmedLabels) => {
    if (!confirmedLabels.length) return;
    setSubmitting(true);
    const res = await dispatch(
      bulkMarkCleanedAndAssign({
        hvac_label_numbers: confirmedLabels,
        to_area: markCleanedArea
      })
    );
    setSubmitting(false);
    if (res?.payload?.success) {
      flashRows(confirmedLabels);
      clearSelection();
      refreshAll();
      closeModal();
    }
  };

  // ---- Bulk: Install from Spare Stock (2 steps) ----
  const handleInstallStep1 = () => {
    if (!installArea) return;
    setInstallStep('scan');
  };
  const handleInstallWithScan = async (confirmedLabels) => {
    if (!confirmedLabels.length) return;
    setSubmitting(true);
    const res = await dispatch(
      bulkInstallFromSpare({
        hvac_label_numbers: confirmedLabels,
        to_area: installArea
      })
    );
    setSubmitting(false);
    if (res?.payload?.success) {
      flashRows(confirmedLabels);
      clearSelection();
      refreshAll();
      closeModal();
    }
  };

  // ---- Bulk: Inactivate ----
  const handleBulkInactivate = async (values) => {
    setSubmitting(true);
    const res = await dispatch(
      bulkInactivateHvac({
        hvac_label_numbers: selectedLabels,
        reason: values.reason
      })
    );
    setSubmitting(false);
    if (res?.payload?.success) {
      flashRows(selectedLabels);
      clearSelection();
      refreshAll();
      closeModal();
      inactivateForm.resetFields();
    }
  };

  // ---- Reactivate ----
  const handleReactivate = async (values) => {
    setSubmitting(true);
    const res = await dispatch(
      reactivateHvac({
        hvacLabelNumber: reactivateHvacLabel,
        note: values.note
      })
    );
    setSubmitting(false);
    if (res?.payload?.success) {
      flashRows([reactivateHvacLabel]);
      setReactivateHvacLabel(null);
      refreshAll();
      setModalType(null);
      reactivateForm.resetFields();
    }
  };

  // ---- Per-row log modal ----
  const openLogModal = (record) => {
    setLogHvacLabel(record.hvac_label_number);
    dispatch(getHvacLogs(record.hvac_label_number));
    setModalType('hvac_log');
  };

  const handleDownloadHvacPdf = () => {
    dispatch(
      generateHvacUsageLogReport({
        hvac_label_number: logHvacLabel,
        flat: false,
        from_date: moment().subtract(1, 'year').format('YYYY-MM-DD'),
        to_date: moment().format('YYYY-MM-DD')
      })
    );
  };

  // ---- Usage Log Report ----
  const handleGenerateReport = async (values) => {
    const from_date = values.from_date.format('YYYY-MM-DD');
    const to_date = values.to_date.format('YYYY-MM-DD');
    await dispatch(
      generateHvacUsageLogReport({ from_date, to_date, flat: true })
    );
    setModalType(null);
    reportForm.resetFields();
  };

  // ---- Regenerate missing PDFs ----
  const [regenerating, setRegenerating] = useState(false);
  const handleRegenerateMissing = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      await dispatch(regenerateMissingHvacPdfs());
      fetchList();
    } finally {
      setRegenerating(false);
    }
  };

  const emptyText = useMemo(() => {
    if (scanFilter)
      return `No HVAC filters found for the scanned filter (${
        scanFilter.length
      } label${scanFilter.length !== 1 ? 's' : ''})`;
    if (labelSearch) return `No HVAC filters match "${labelSearch}"`;
    if (areaSearchInput) return `No HVAC filters in area "${areaSearchInput}"`;
    if (activeTab !== 'all') return 'No HVAC filters in this status yet';
    return 'No HVAC filter labels have been created yet';
  }, [labelSearch, areaSearchInput, activeTab, scanFilter]);

  // Area dropdown options
  const allAreaOptions = useMemo(
    () => (areas || []).map((a) => ({ value: a, label: a })),
    [areas]
  );
  const manufacturingAreaOptions = useMemo(
    () =>
      (areas || [])
        .filter((a) => String(a).toLowerCase() !== 'spare stock')
        .map((a) => ({ value: a, label: a })),
    [areas]
  );

  const columns = useMemo(
    () => [
      {
        title: 'HVAC Label',
        dataIndex: 'hvac_label_number',
        key: 'hvac_label_number',
        width: 130,
        defaultSortOrder: 'ascend',
        sorter: (a, b) => {
          const na = parseInt(a.hvac_label_number.replace(/\D/g, ''), 10);
          const nb = parseInt(b.hvac_label_number.replace(/\D/g, ''), 10);
          return na - nb;
        },
        render: (v) => (
          <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{v}</span>
        )
      },
      {
        title: 'In-house Number',
        dataIndex: 'inhouse_number',
        key: 'inhouse_number',
        width: 160,
        ellipsis: { showTitle: true },
        render: (v) => v || '—'
      },
      {
        title: 'Status',
        key: 'status',
        width: 140,
        render: (_, record) => {
          const s = getStatusFromRecord(record);
          const cfg = STATUS_CONFIG[s];
          return <Tag color={cfg.color}>{cfg.label}</Tag>;
        }
      },
      {
        title: 'Current Area',
        dataIndex: 'current_area',
        key: 'current_area',
        width: 200,
        ellipsis: { showTitle: true },
        sorter: (a, b) =>
          (a.current_area || '').localeCompare(b.current_area || ''),
        render: (v) => {
          if (!v) return '—';
          const isSpare = String(v).toLowerCase() === 'spare stock';
          return (
            <Tag color={isSpare ? 'green' : 'blue'} style={{ marginRight: 0 }}>
              {v}
            </Tag>
          );
        }
      },
      {
        title: 'Last Cleaned',
        dataIndex: 'cleaned_at',
        key: 'cleaned_at',
        width: 170,
        render: (v) =>
          v ? moment(v).utcOffset('+05:30').format('DD MMM YYYY HH:mm') : '—'
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 170,
        sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
        render: (v) =>
          v ? moment(v).utcOffset('+05:30').format('DD MMM YYYY HH:mm') : '—'
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 110,
        fixed: 'right',
        render: (_, record) => (
          <Space size={8}>
            <Tooltip
              title={record.pdf_url ? 'Download Label PDF' : 'PDF not ready'}
            >
              <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                href={
                  record.pdf_url ? buildBlobPdfUrl(record.pdf_url) : undefined
                }
                target="_blank"
                rel="noopener noreferrer"
                download={
                  record.pdf_url ? `${record.hvac_label_number}.pdf` : undefined
                }
                disabled={!record.pdf_url}
                style={{ padding: 0 }}
              />
            </Tooltip>
            <Tooltip title="View Activity Log">
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => openLogModal(record)}
                style={{ padding: 0 }}
              />
            </Tooltip>
            {record.is_retired && canReactivate && (
              <Tooltip title="Reactivate Filter">
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, color: '#52c41a' }}
                  onClick={() => {
                    setReactivateHvacLabel(record.hvac_label_number);
                    reactivateForm.resetFields();
                    setModalType('reactivate');
                  }}
                >
                  Reactivate
                </Button>
              </Tooltip>
            )}
            {!record.pdf_url &&
              record.createdAt &&
              new Date() - new Date(record.createdAt) > 90 * 1000 && (
                <Tooltip title="PDF stalled — retry">
                  <Button
                    type="link"
                    size="small"
                    icon={<WarningOutlined style={{ color: '#faad14' }} />}
                    onClick={handleRegenerateMissing}
                    style={{ padding: 0 }}
                  />
                </Tooltip>
              )}
          </Space>
        )
      }
    ],
    [page, perPage, canReactivate, regenerating]
  );

  const logColumns = [
    {
      title: 'Timestamp (IST)',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 190,
      render: (v) =>
        v
          ? moment(v).utcOffset('+05:30').format('DD MMM YYYY HH:mm:ss') +
            ' IST'
          : '—'
    },
    {
      title: 'Activity',
      dataIndex: 'action',
      key: 'action',
      width: 180,
      render: (v) => ACTION_LABELS[v] || v
    },
    {
      title: 'Performed By',
      dataIndex: 'performed_by_name',
      key: 'performed_by_name',
      width: 160
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
      render: (v, record) => formatDetails(record.action, v) || '—'
    }
  ];

  const bulkTooltip = isMixed
    ? 'Select HVAC filters of the same status to perform a bulk action'
    : selectedItems.length === 0
    ? 'Select HVAC filters first'
    : '';

  return (
    <Layout sider={false}>
      <div className={styles.page_wrapper}>
        {/* Header row */}
        <Row
          justify="space-between"
          align="middle"
          className={styles.page_header_row}
        >
          <Col>
            <Space align="center">
              <h2 className={styles.page_title}>HVAC Filter Allocation</h2>
              {pendingCount > 0 && (
                <span
                  className={
                    stalePendingCount > 0
                      ? styles.pending_chip_warn
                      : styles.pending_chip
                  }
                  title={
                    stalePendingCount > 0
                      ? 'One or more labels may have stalled'
                      : 'Labels are generating in the background'
                  }
                >
                  {stalePendingCount > 0 ? (
                    <WarningOutlined />
                  ) : (
                    <LoadingOutlined spin />
                  )}{' '}
                  {pendingCount}{' '}
                  {stalePendingCount > 0 ? 'may have stalled' : 'generating'}
                  {stalePendingCount > 0 && (
                    <Button
                      type="link"
                      size="small"
                      onClick={handleRegenerateMissing}
                      loading={regenerating}
                      disabled={regenerating}
                      className={styles.pending_retry}
                    >
                      Retry
                    </Button>
                  )}
                </span>
              )}
            </Space>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<ScanOutlined />}
                type={scanFilter ? 'primary' : 'default'}
                onClick={() => setBulkScanModalOpen(true)}
              >
                Scan to Filter
              </Button>
              {scanFilter && (
                <Tag
                  closable
                  onClose={() => {
                    setScanFilter(null);
                    setPage(1);
                  }}
                  color="blue"
                  style={{ fontSize: 12 }}
                >
                  {scanFilter.length} filter{scanFilter.length !== 1 ? 's' : ''}{' '}
                  filtered
                </Tag>
              )}
              <Button
                icon={<FileTextOutlined />}
                onClick={() => {
                  reportForm.resetFields();
                  setModalType('usage_log');
                }}
              >
                HVAC Activity Log
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Info Cards */}
        <Row gutter={16} className={styles.cards_row}>
          <Col xs={24} sm={6}>
            <Card
              className={`${styles.info_card} ${styles.card_blue}`}
              onClick={() => handleCardClick('in_use')}
            >
              <Statistic title="In Use" value={counts.in_use || 0} />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card
              className={`${styles.info_card} ${styles.card_green}`}
              onClick={() => handleCardClick('in_spare_stock')}
            >
              <Statistic
                title="In Spare Stock"
                value={counts.in_spare_stock || 0}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card
              className={`${styles.info_card} ${styles.card_orange}`}
              onClick={() => handleCardClick('to_be_cleaned')}
            >
              <Statistic
                title="To Be Cleaned"
                value={counts.to_be_cleaned || 0}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card
              className={`${styles.info_card} ${styles.card_grey}`}
              onClick={() => handleCardClick('inactive')}
            >
              <Statistic title="Inactive" value={counts.inactive || 0} />
            </Card>
          </Col>
        </Row>

        {/* Tab slicer */}
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          className={styles.tab_slicer}
          tabBarExtraContent={
            selectedItems.length > 0
              ? (() => {
                  const tally = {};
                  selectedItems.forEach((i) => {
                    const label = STATUS_CONFIG[i.status]?.label || i.status;
                    tally[label] = (tally[label] || 0) + 1;
                  });
                  const parts = Object.entries(tally).map(
                    ([label, n]) => `${n} ${label}`
                  );
                  return (
                    <span className={styles.selection_badge}>
                      {selectedItems.length} selected — {parts.join(' · ')}
                    </span>
                  );
                })()
              : null
          }
        >
          <TabPane tab="All" key="all" />
          <TabPane tab={`In Use (${counts.in_use || 0})`} key="in_use" />
          <TabPane
            tab={`In Spare Stock (${counts.in_spare_stock || 0})`}
            key="in_spare_stock"
          />
          <TabPane
            tab={`To Be Cleaned (${counts.to_be_cleaned || 0})`}
            key="to_be_cleaned"
          />
          <TabPane tab={`Inactive (${counts.inactive || 0})`} key="inactive" />
        </Tabs>

        {/* Toolbar: search + bulk actions */}
        <Row
          justify="space-between"
          align="middle"
          className={styles.toolbar_row}
          gutter={[8, 8]}
        >
          <Col xs={24} md={12} lg={10}>
            <Space align="center" wrap>
              <Input.Search
                placeholder="Search by HVAC label or in-house number"
                allowClear
                value={labelSearch}
                onChange={(e) => !e.target.value && setLabelSearch('')}
                onSearch={(v) => {
                  setPage(1);
                  setLabelSearch(v);
                }}
                style={{ width: 240 }}
              />
              <Input.Search
                placeholder="Filter by area"
                allowClear
                value={areaSearchInput}
                onChange={(e) => !e.target.value && setAreaSearchInput('')}
                onSearch={(v) => {
                  setPage(1);
                  setAreaSearchInput(v);
                }}
                style={{ width: 180 }}
              />
              {hasActiveFilters && (
                <Button
                  type="text"
                  size="small"
                  icon={<CloseCircleOutlined />}
                  onClick={handleClearFilters}
                  style={{ color: '#ff4d4f' }}
                >
                  Clear Filters
                </Button>
              )}
            </Space>
          </Col>
          <Col>
            <Space align="center" wrap>
              <Tooltip title={bulkTooltip}>
                <Button
                  style={
                    canMarkCleanedAssign
                      ? {
                          background: '#f6ffed',
                          borderColor: '#52c41a',
                          color: '#52c41a'
                        }
                      : {}
                  }
                  disabled={!canMarkCleanedAssign || !canAct}
                  onClick={() => {
                    setMarkCleanedArea(null);
                    setMarkCleanedStep('area');
                    setModalType('mark_cleaned');
                  }}
                >
                  Mark Cleaned & Assign
                </Button>
              </Tooltip>
              <Tooltip title={bulkTooltip}>
                <Button
                  type="primary"
                  disabled={!canInstallFromSpare || !canAct}
                  onClick={() => {
                    setInstallArea(null);
                    setInstallStep('area');
                    setModalType('install_from_spare');
                  }}
                >
                  Install from Spare Stock
                </Button>
              </Tooltip>
              <Tooltip title={bulkTooltip}>
                <Button
                  danger
                  disabled={!canSendForCleaning || !canAct}
                  onClick={() => setModalType('send_cleaning')}
                >
                  Send for Cleaning
                </Button>
              </Tooltip>
              <Tooltip
                title={
                  !canInactivate
                    ? 'Select active filters of the same status'
                    : ''
                }
              >
                <Button
                  icon={<StopOutlined />}
                  disabled={!canInactivate || !canAct}
                  onClick={() => {
                    inactivateForm.resetFields();
                    setModalType('bulk_inactivate');
                  }}
                >
                  Inactivate
                </Button>
              </Tooltip>
            </Space>
          </Col>
        </Row>

        {/* Main table */}
        <div className={submitting ? styles.table_locked : undefined}>
          <CustomTable
            columns={columns}
            data={list}
            rowKey={(r) => r.id}
            rowSelection={rowSelection}
            loading={loading}
            scroll={{ x: 'max-content', y: 600 }}
            rowClassName={(record) => {
              if (highlightedLabels.has(record.hvac_label_number))
                return styles.row_highlight;
              if (record.is_retired || !record.is_active)
                return styles.inactive_row;
              return '';
            }}
            locale={{ emptyText: <Empty description={emptyText} /> }}
            pagination={{
              total: listPagination?.total_records || 0,
              pageSize: perPage,
              current: page,
              onChange: handlePageChange
            }}
            showPaginationMeta
          />
        </div>

        {/* ── MODALS ── */}

        {/* Send for Cleaning confirmation */}
        <CustomModal
          title={`Send ${selectedIds.length} HVAC Filter${
            selectedIds.length > 1 ? 's' : ''
          } for Cleaning`}
          visible={modalType === 'send_cleaning'}
          onCancel={closeModal}
          okText="Confirm"
          cancelText="Cancel"
          okButtonProps={{
            loading: submitting,
            disabled: submitting,
            danger: true,
            onClick: handleBulkSendForCleaning
          }}
          cancelButtonProps={{ disabled: submitting, onClick: closeModal }}
        >
          <p>
            These filters will be removed from their current areas and moved to
            the cleaning queue. Are you sure?
          </p>
          <HvacLabelList labels={selectedLabels} />
        </CustomModal>

        {/* Mark Cleaned & Assign — Step 1: pick destination area */}
        <CustomModal
          title={`Mark ${selectedIds.length} Filter${
            selectedIds.length > 1 ? 's' : ''
          } Cleaned & Assign`}
          visible={modalType === 'mark_cleaned' && markCleanedStep === 'area'}
          onCancel={closeModal}
          customFooter={false}
          footer={null}
          width={520}
        >
          <p style={{ marginBottom: 8, color: '#595959' }}>
            Pick the destination area for these{' '}
            <strong>{selectedIds.length}</strong> cleaned filter
            {selectedIds.length > 1 ? 's' : ''} — Spare Stock or any
            manufacturing area.
          </p>
          <HvacLabelList labels={selectedLabels} />
          <Form layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item label="Destination Area" required>
              <Select
                showSearch
                placeholder="Select destination area"
                optionFilterProp="label"
                value={markCleanedArea}
                onChange={setMarkCleanedArea}
                options={allAreaOptions}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Button onClick={closeModal} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleMarkCleanedStep1}
                disabled={!markCleanedArea || submitting}
                style={{ marginLeft: 8 }}
              >
                Next: Scan Filters →
              </Button>
            </div>
          </Form>
        </CustomModal>

        {/* Mark Cleaned & Assign — Step 2: QR scan verification */}
        <HvacScanModal
          scannerId="hvac-scan-mark-cleaned"
          visible={modalType === 'mark_cleaned' && markCleanedStep === 'scan'}
          title={`Scan Filters — destination: ${markCleanedArea || ''}`}
          targetLabels={selectedLabels}
          confirmButtonText="Mark Cleaned & Assign"
          loading={submitting}
          onCancel={() => setMarkCleanedStep('area')}
          onConfirm={handleMarkCleanedWithScan}
        />

        {/* Install from Spare Stock — Step 1: pick manufacturing area */}
        <CustomModal
          title={`Install ${selectedIds.length} Filter${
            selectedIds.length > 1 ? 's' : ''
          } from Spare Stock`}
          visible={modalType === 'install_from_spare' && installStep === 'area'}
          onCancel={closeModal}
          customFooter={false}
          footer={null}
          width={520}
        >
          <p style={{ marginBottom: 8, color: '#595959' }}>
            Pick the manufacturing area to install these{' '}
            <strong>{selectedIds.length}</strong> Spare Stock filter
            {selectedIds.length > 1 ? 's' : ''} into. Spare Stock is excluded.
          </p>
          <HvacLabelList labels={selectedLabels} />
          <Form layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item label="Destination Manufacturing Area" required>
              <Select
                showSearch
                placeholder="Select manufacturing area"
                optionFilterProp="label"
                value={installArea}
                onChange={setInstallArea}
                options={manufacturingAreaOptions}
                style={{ width: '100%' }}
                notFoundContent={
                  manufacturingAreaOptions.length === 0
                    ? 'No manufacturing areas yet — create a filter at a manufacturing area first.'
                    : null
                }
              />
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Button onClick={closeModal} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleInstallStep1}
                disabled={!installArea || submitting}
                style={{ marginLeft: 8 }}
              >
                Next: Scan Filters →
              </Button>
            </div>
          </Form>
        </CustomModal>

        {/* Install from Spare Stock — Step 2: QR scan */}
        <HvacScanModal
          scannerId="hvac-scan-install"
          visible={modalType === 'install_from_spare' && installStep === 'scan'}
          title={`Scan Filters — installing at: ${installArea || ''}`}
          targetLabels={selectedLabels}
          confirmButtonText="Install"
          loading={submitting}
          onCancel={() => setInstallStep('area')}
          onConfirm={handleInstallWithScan}
        />

        {/* Bulk Inactivate */}
        <CustomModal
          title={`Inactivate ${selectedIds.length} HVAC Filter${
            selectedIds.length > 1 ? 's' : ''
          }`}
          visible={modalType === 'bulk_inactivate'}
          onCancel={closeModal}
          customFooter={false}
          footer={null}
          width={480}
        >
          <Alert
            type="warning"
            showIcon
            message="Inactivated HVAC filters cannot be used for any operations."
            style={{ marginBottom: 8 }}
          />
          <HvacLabelList labels={selectedLabels} />
          <Form
            form={inactivateForm}
            style={{ marginTop: 16 }}
            layout="vertical"
            onFinish={handleBulkInactivate}
          >
            <Form.Item
              label="Reason for Inactivation"
              name="reason"
              rules={[
                { required: true, message: 'Reason is required.' },
                { min: 10, message: 'Please provide at least 10 characters.' }
              ]}
            >
              <Input.TextArea
                rows={3}
                maxLength={500}
                showCount
                placeholder="e.g. HVAC filter physically damaged and failed inspection"
              />
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Button onClick={closeModal} disabled={submitting}>
                Cancel
              </Button>
              <Button
                danger
                htmlType="submit"
                loading={submitting}
                disabled={submitting}
                style={{ marginLeft: 8 }}
              >
                Confirm Inactivation
              </Button>
            </div>
          </Form>
        </CustomModal>

        {/* Reactivate */}
        <CustomModal
          title={`Reactivate HVAC Filter ${reactivateHvacLabel || ''}`}
          visible={modalType === 'reactivate'}
          onCancel={() => {
            setModalType(null);
            reactivateForm.resetFields();
          }}
          customFooter={false}
          footer={null}
          width={440}
        >
          <Alert
            type="info"
            showIcon
            message="The filter will be returned to Spare Stock."
            style={{ marginBottom: 8 }}
          />
          <HvacLabelList
            labels={reactivateHvacLabel ? [reactivateHvacLabel] : []}
          />
          <Form
            form={reactivateForm}
            layout="vertical"
            onFinish={handleReactivate}
            style={{ marginTop: 16 }}
          >
            <Form.Item
              label="Reactivation Note"
              name="note"
              rules={[
                { required: true, message: 'Note is required.' },
                { min: 10, message: 'Please provide at least 10 characters.' }
              ]}
            >
              <Input.TextArea
                rows={3}
                maxLength={500}
                showCount
                placeholder="e.g. Filter repaired and cleared after inspection"
              />
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Button
                onClick={() => {
                  setModalType(null);
                  reactivateForm.resetFields();
                }}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={submitting}
                style={{ marginLeft: 8 }}
                icon={<CheckCircleOutlined />}
              >
                Reactivate
              </Button>
            </div>
          </Form>
        </CustomModal>

        {/* Per-row Activity Log modal */}
        <CustomModal
          title={`HVAC Filter ${logHvacLabel || ''} — Activity Log`}
          visible={modalType === 'hvac_log'}
          onCancel={closeModal}
          customFooter={false}
          footer={null}
          width={820}
        >
          {hvacLogsLoading ? (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <Spin tip="Loading activity log..." />
            </div>
          ) : (
            <div className={styles.log_table}>
              <Table
                columns={logColumns}
                dataSource={hvacLogs}
                rowKey="id"
                pagination={false}
                size="small"
                scroll={{ x: 'max-content' }}
                locale={{
                  emptyText: (
                    <Empty description="No activity recorded for this filter yet." />
                  )
                }}
              />
            </div>
          )}
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadHvacPdf}
              loading={reportGenerating}
              style={{ marginRight: 8 }}
            >
              Download PDF
            </Button>
            <Button onClick={closeModal}>Close</Button>
          </div>
        </CustomModal>

        {/* Usage Log Report */}
        <CustomModal
          title="Generate HVAC Filter Activity Log"
          visible={modalType === 'usage_log'}
          onCancel={closeModal}
          customFooter={false}
          footer={null}
          width={440}
        >
          <Form
            form={reportForm}
            layout="vertical"
            onFinish={handleGenerateReport}
          >
            <Form.Item
              label="From Date"
              name="from_date"
              rules={[{ required: true, message: 'From date is required' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                disabledDate={(d) => d && d.isAfter(moment(), 'day')}
                placeholder="Select from date"
              />
            </Form.Item>
            <Form.Item
              label="To Date"
              name="to_date"
              rules={[
                { required: true, message: 'To date is required' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || !getFieldValue('from_date'))
                      return Promise.resolve();
                    if (value.isBefore(getFieldValue('from_date'), 'day')) {
                      return Promise.reject(
                        new Error('To date cannot be before from date')
                      );
                    }
                    return Promise.resolve();
                  }
                })
              ]}
            >
              <DatePicker
                style={{ width: '100%' }}
                disabledDate={(d) => d && d.isAfter(moment(), 'day')}
                placeholder="Select to date"
              />
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Button onClick={closeModal}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={reportGenerating}
                disabled={reportGenerating}
                style={{ marginLeft: 8 }}
              >
                Generate Report
              </Button>
            </div>
          </Form>
        </CustomModal>

        {/* Bulk Scan to Filter */}
        <BulkHvacScanFilterModal
          visible={bulkScanModalOpen}
          onCancel={() => setBulkScanModalOpen(false)}
          onApply={(labels) => {
            setScanFilter(labels);
            setPage(1);
            setBulkScanModalOpen(false);
          }}
        />
      </div>
    </Layout>
  );
};

export default HvacFilterAllocation;
